import { LoadSpinner } from '@calizahq/react-components-v2'
import { Formik } from 'formik'
import React, { useState } from 'react'

import { HomeIssueMutations } from '@/graphql/mutations/homeIssue.mutations'
import { HomeInventoryItemQueries } from '@/graphql/queries/homeInventoryItems.queries'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useGraphql, useGraphqlMutation } from '@/hooks/useGraphql'
import useHomeLocations from '@/hooks/useHomeLocations/useHomeLocations'
import { HomeInventoryItem } from '@/types/HomeInventoryItem'
import { NewHomeIssue } from '@/types/HomeIssue'

import { HomeIssueForm } from './HomeIssueForm'
import { formSchema, FormValuesType, initialValues } from './ReportIssue.util'
import { ReportIssueConfirmation } from './ReportIssueConfirmation'

type Props = {
  homeId: number
  homeQualityCheckId?: number
  refetchOnSuccess?: () => void
  toggle: () => void
}

export const ReportIssueForm = ({ toggle, homeId, homeQualityCheckId, refetchOnSuccess }: Props) => {
  const [error, setError] = useState<string>()
  const [homeIssue, setHomeIssue] = useState<NewHomeIssue>()
  const [isCapturing, setIsCapturing] = useState(false)
  const { captureGeolocation } = useGeolocation()

  const { data: homeInventoryItemData, isPending: isHomeInventoryItemLoading } = useGraphql<{
    homeInventoryItems: HomeInventoryItem[]
  }>(HomeInventoryItemQueries.QUERY_KEY, HomeInventoryItemQueries.getHomeInventoryItems())

  const { data: homeLocationData, isPending: isHomeLocationLoading } = useHomeLocations()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(HomeIssueMutations.createHomeIssue(), {
    onSuccess: (res: { createHomeIssue: NewHomeIssue }) => {
      setHomeIssue(res.createHomeIssue)
      refetchOnSuccess && refetchOnSuccess()
    },
    onError: () => {
      setError('Error reporting Home Issue.')
    },
  })

  const homeInventoryItemOptions =
    homeInventoryItemData && homeInventoryItemData.homeInventoryItems
      ? homeInventoryItemData.homeInventoryItems.map((homeInventoryItem) => {
          return { label: homeInventoryItem.name, value: homeInventoryItem.id }
        })
      : []

  const homeLocationOptions =
    homeLocationData && homeLocationData.homeLocations
      ? homeLocationData.homeLocations.map((homeLocation) => {
          return { label: homeLocation.name, value: homeLocation.id }
        })
      : []

  const isLoading = isHomeInventoryItemLoading || isHomeLocationLoading

  const handleSubmit = async (values: FormValuesType) => {
    if (isCapturing) return
    setIsCapturing(true)
    try {
      const { attachments, ...restOfValues } = values
      const geolocation = await captureGeolocation()
      mutate({
        homeIssue: {
          ...restOfValues,
        },
        attachments,
        geolocation,
      })
    } finally {
      setIsCapturing(false)
    }
  }

  return (
    <>
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {!homeIssue ? (
            <Formik
              enableReinitialize={true}
              initialValues={{
                ...initialValues,
                homeId: homeId,
                homeQualityCheckId: homeQualityCheckId,
              }}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validationSchema={formSchema}
            >
              {({ values }) => {
                return (
                  <HomeIssueForm
                    error={error}
                    homeInventoryItemOptions={homeInventoryItemOptions}
                    homeIssueType={values.type}
                    homeLocationOptions={homeLocationOptions}
                    isCapturing={isCapturing}
                    isMutationLoading={isMutationLoading}
                    toggle={toggle}
                  />
                )
              }}
            </Formik>
          ) : (
            <ReportIssueConfirmation homeIssue={homeIssue} toggle={toggle} />
          )}
        </>
      )}
    </>
  )
}
